/**
 * ChannelItem Component
 *
 * Reusable component for displaying a notification channel (email, Slack, webhook).
 * Provides consistent visual treatment and action buttons across channel types.
 *
 * Design Compliance:
 * - Polaris spacing scale (gap="200", gap="300")
 * - Color with purpose (border-brand for enabled, success/warning for status)
 * - Icons left of labels with consistent 35px container
 * - Action buttons: Test, Verify (email), Enable/Disable, Delete
 *
 * @example
 * ```tsx
 * <ChannelItem
 *   channel={channel}
 *   onTest={handleTest}
 *   onToggle={handleToggle}
 *   onDelete={handleDelete}
 *   onVerify={handleVerify}
 * />
 * ```
 */

import { useState, useCallback } from 'react';
import { BlockStack, InlineStack, Text, Button, Icon, Box, Tooltip, Popover, ActionList } from '@shopify/polaris';
import { EmailIcon, DeleteIcon, NotificationIcon, ClipboardIcon, MenuHorizontalIcon, PlayIcon, CheckIcon } from '@shopify/polaris-icons';
import type { NotificationChannel } from '../../dashboard/NotificationChannels';
import { AppBridgeInlineModal } from '../../app-bridge/AppBridgeModal';

// Slack SVG icon component
function SlackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  );
}

// Webhook SVG icon component
function WebhookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10.5 7.5c0-1.657 1.343-3 3-3s3 1.343 3 3-1.343 3-3 3c-.462 0-.9-.105-1.291-.292l-1.709 2.96A4.5 4.5 0 1 1 6.5 9.5h3.367A2.99 2.99 0 0 1 10.5 7.5zm-3 9c-.93 0-1.767-.424-2.318-1.091l-2.6 1.5a.75.75 0 1 1-.75-1.299l2.6-1.5A2.987 2.987 0 0 1 4.5 13.5c0-1.657 1.343-3 3-3s3 1.343 3 3a2.99 2.99 0 0 1-.633 1.841l2.633 4.559a.75.75 0 1 1-1.299.75l-2.633-4.559a3.013 3.013 0 0 1-1.068.409zm6-6c1.657 0 3 1.343 3 3a2.99 2.99 0 0 1-.633 1.841l2.633 4.559a.75.75 0 1 1-1.299.75l-2.633-4.559A3.013 3.013 0 0 1 13.5 16.5c-1.657 0-3-1.343-3-3s1.343-3 3-3z"/>
    </svg>
  );
}

export interface ChannelItemProps {
  /** The notification channel to display */
  channel: NotificationChannel;
  /** Handler for testing the channel */
  onTest: (channel: NotificationChannel) => void;
  /** Handler for toggling the channel enabled state */
  onToggle: (channel: NotificationChannel) => void;
  /** Handler for deleting the channel */
  onDelete: (channel: NotificationChannel) => void;
  /** Handler for sending verification (email only) */
  onVerify?: (channel: NotificationChannel) => void;
  /** Callback when copy-to-clipboard succeeds */
  onCopySuccess?: (message: string) => void;
  /** Whether actions are loading */
  isLoading?: boolean;
}

/**
 * Get the appropriate icon for a channel type
 */
function getChannelIcon(type: string) {
  if (type === 'email') return EmailIcon;
  return NotificationIcon;
}

/**
 * Get the background color for the icon container
 * Uses Polaris semantic tokens for consistency with the design system
 */
function getIconBackground() {
  // All channel types use the same neutral background
  // This follows Polaris principle: color should have purposeful meaning
  // Different channel types are distinguished by their icons, not background color
  return 'var(--p-color-bg-surface-secondary)';
}

/**
 * Get human-readable label for channel type
 */
function getChannelTypeLabel(type: string) {
  if (type === 'email') return 'Email';
  if (type === 'slack') return 'Slack';
  if (type === 'webhook') return 'Webhook';
  return type;
}

/**
 * Get the channel detail text based on type
 */
function getChannelDetail(channel: NotificationChannel) {
  if (channel.channel_type === 'email' && channel.config?.email_address) {
    return channel.config.email_address;
  }
  if (channel.channel_type === 'slack') {
    return 'Slack workspace integration';
  }
  if (channel.channel_type === 'webhook' && channel.config?.url) {
    // Truncate long URLs
    const url = channel.config.url;
    return url.length > 50 ? `${url.substring(0, 47)}...` : url;
  }
  return null;
}

export function ChannelItem({
  channel,
  onTest,
  onToggle,
  onDelete,
  onVerify,
  onCopySuccess,
  isLoading = false,
}: ChannelItemProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const isEmail = channel.channel_type === 'email';
  const isSlack = channel.channel_type === 'slack';
  const isWebhook = channel.channel_type === 'webhook';
  const needsVerification = isEmail && !channel.is_verified;
  const channelDetail = getChannelDetail(channel);

  const handleDeleteClick = useCallback(() => {
    setShowActionsMenu(false);
    setShowDeleteConfirmation(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    onDelete(channel);
    setShowDeleteConfirmation(false);
  }, [channel, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  const handleCopyUrl = useCallback(() => {
    if (isWebhook && channel.config?.url) {
      navigator.clipboard.writeText(channel.config.url);
      onCopySuccess?.('Webhook URL copied to clipboard');
    }
    setShowActionsMenu(false);
  }, [isWebhook, channel.config?.url, onCopySuccess]);

  const handleTestClick = useCallback(() => {
    setShowActionsMenu(false);
    onTest(channel);
  }, [channel, onTest]);

  const handleToggleClick = useCallback(() => {
    setShowActionsMenu(false);
    onToggle(channel);
  }, [channel, onToggle]);

  const handleVerifyClick = useCallback(() => {
    setShowActionsMenu(false);
    onVerify?.(channel);
  }, [channel, onVerify]);

  const toggleActionsMenu = useCallback(() => {
    setShowActionsMenu((prev) => !prev);
  }, []);

  // Render the appropriate icon
  const renderIcon = () => {
    if (isSlack) {
      return <SlackIcon />;
    }
    if (isWebhook) {
      return <WebhookIcon />;
    }
    return <Icon source={getChannelIcon(channel.channel_type)} tone="base" />;
  };

  return (
    <Box
      background="bg-surface"
      padding="300"
      borderColor={channel.enabled ? 'border-brand' : 'border'}
      borderWidth="025"
      borderRadius="200"
    >
      <InlineStack gap="300" blockAlign="start" wrap={false}>
        {/* Channel Icon */}
        <div
          style={{
            width: '35px',
            height: '35px',
            borderRadius: '7px',
            backgroundColor: getIconBackground(),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {renderIcon()}
        </div>

        {/* Channel Info and Actions */}
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center" wrap>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              {channel.channel_name}
            </Text>

            {/* Channel Type Badge */}
            <Box
              background="bg-surface-secondary"
              padding="100"
              paddingInlineStart="200"
              paddingInlineEnd="200"
              borderColor="border"
              borderWidth="025"
              borderRadius="100"
            >
              <Text as="span" variant="bodySm" tone="subdued">
                {getChannelTypeLabel(channel.channel_type)}
              </Text>
            </Box>

            {/* Verification Status (email only) */}
            {isEmail && channel.is_verified && (
              <Box
                background="bg-fill-success-secondary"
                padding="100"
                paddingInlineStart="200"
                paddingInlineEnd="200"
                borderRadius="100"
              >
                <Text as="span" variant="bodySm" tone="success">
                  Verified
                </Text>
              </Box>
            )}

            {needsVerification && (
              <Box
                background="bg-fill-warning-secondary"
                padding="100"
                paddingInlineStart="200"
                paddingInlineEnd="200"
                borderRadius="100"
              >
                <Text as="span" variant="bodySm" tone="caution">
                  Unverified
                </Text>
              </Box>
            )}

            {/* Disabled Badge */}
            {!channel.enabled && (
              <Box
                background="bg-surface-secondary"
                padding="100"
                paddingInlineStart="200"
                paddingInlineEnd="200"
                borderRadius="100"
              >
                <Text as="span" variant="bodySm" tone="subdued">
                  Disabled
                </Text>
              </Box>
            )}
          </InlineStack>

          {/* Channel Details */}
          {channelDetail && (
            <InlineStack gap="200" blockAlign="center">
              <Text as="p" variant="bodySm" tone="subdued">
                {channelDetail}
                {channel.last_used_at && <span suppressHydrationWarning>{` - Last used ${new Date(channel.last_used_at).toLocaleDateString()}`}</span>}
              </Text>
              {/* Copy button for webhook URLs */}
              {isWebhook && channel.config?.url && (
                <Tooltip content="Copy webhook URL">
                  <Button
                    size="slim"
                    variant="plain"
                    icon={ClipboardIcon}
                    onClick={handleCopyUrl}
                    accessibilityLabel="Copy webhook URL"
                  />
                </Tooltip>
              )}
            </InlineStack>
          )}

          {/* Action Buttons - Desktop (hidden on mobile) */}
          <div className="channel-actions-desktop">
            <InlineStack gap="200">
              {/* Verify button (email only, when unverified) */}
              {needsVerification && onVerify && (
                <Button size="slim" onClick={handleVerifyClick} loading={isLoading}>
                  Verify
                </Button>
              )}

              {/* Test button (only for verified channels) */}
              {channel.is_verified && (
                <Button size="slim" onClick={handleTestClick} loading={isLoading}>
                  Test
                </Button>
              )}

              {/* Non-email channels can always be tested */}
              {!isEmail && !channel.is_verified && (
                <Button size="slim" onClick={handleTestClick} loading={isLoading}>
                  Test
                </Button>
              )}

              {/* Enable/Disable toggle */}
              <Button size="slim" onClick={handleToggleClick}>
                {channel.enabled ? 'Disable' : 'Enable'}
              </Button>

              {/* Delete button */}
              <Button
                size="slim"
                icon={DeleteIcon}
                onClick={handleDeleteClick}
                tone="critical"
                accessibilityLabel={`Delete ${channel.channel_name}`}
              />
            </InlineStack>
          </div>

          {/* Action Menu - Mobile (hidden on desktop) */}
          <div className="channel-actions-mobile">
            <Popover
              active={showActionsMenu}
              activator={
                <Button
                  size="slim"
                  icon={MenuHorizontalIcon}
                  onClick={toggleActionsMenu}
                  accessibilityLabel="Channel actions"
                />
              }
              onClose={toggleActionsMenu}
              preferredAlignment="right"
            >
              <ActionList
                items={[
                  // Verify action (email only, when unverified)
                  ...(needsVerification && onVerify
                    ? [{
                        content: 'Verify',
                        icon: CheckIcon,
                        onAction: handleVerifyClick,
                      }]
                    : []),
                  // Test action
                  ...(channel.is_verified || !isEmail
                    ? [{
                        content: 'Send test',
                        icon: PlayIcon,
                        onAction: handleTestClick,
                      }]
                    : []),
                  // Copy URL (webhook only)
                  ...(isWebhook && channel.config?.url
                    ? [{
                        content: 'Copy URL',
                        icon: ClipboardIcon,
                        onAction: handleCopyUrl,
                      }]
                    : []),
                  // Enable/Disable toggle
                  {
                    content: channel.enabled ? 'Disable' : 'Enable',
                    onAction: handleToggleClick,
                  },
                  // Delete action
                  {
                    content: 'Delete',
                    icon: DeleteIcon,
                    destructive: true,
                    onAction: handleDeleteClick,
                  },
                ]}
              />
            </Popover>
          </div>
        </BlockStack>
      </InlineStack>

      {/* Delete Confirmation Modal - AppBridgeModal for proper admin overlay */}
      <AppBridgeInlineModal
        id={`delete-channel-modal-${channel.id}`}
        open={showDeleteConfirmation}
        onClose={handleDeleteCancel}
        title="Delete notification channel?"
        primaryAction={{
          content: 'Delete channel',
          onAction: handleDeleteConfirm,
          destructive: true,
          loading: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleDeleteCancel,
          },
        ]}
      >
        <BlockStack gap="300">
          <Text as="p" variant="bodyMd">
            Are you sure you want to delete the channel "{channel.channel_name}"?
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            This action cannot be undone. You will no longer receive notifications through this channel.
          </Text>
        </BlockStack>
      </AppBridgeInlineModal>
    </Box>
  );
}
